import {useCallback, useState} from "react";
import {useDispatch} from "react-redux";
import {ProductPurchaseInfo} from "../../res/product/productPurchaseInfo";
import useValueChangeListener from "../../newStructure/services/state/general/useValueChangeListener";
import {reportError} from "../../redux/slice/errorReportSlice";
import useCheckoutStore, {getLineKey} from "../../newStructure/cart/store/checkoutStore";
import useSession from "../../newStructure/services/state/session/useSession";

const useWebProduct = ({orderLineID, product}) => {

    const session = useSession();

    const pid = product?.M_Product_ID?.id ?? product?.id;
    const [ asiID, setAsiIDState ] = useState(0);

    const orderLine = useCheckoutStore(
        useCallback(
            state => orderLineID
                ? state.products?.byID?.[orderLineID]
                : state.products?.byPID?.[pid]?.[asiID],
            [orderLineID, pid, asiID]
        )
    );
    const hasOrderLine = Boolean(orderLine);

    if(orderLineID > 0 && orderLine?.M_AttributeSetInstance_ID?.id !== asiID) {
        setAsiIDState(orderLine?.M_AttributeSetInstance_ID?.id)
    }

    const [ purchaseInfo, setPurchaseInfo ] = useState({
        uomID: orderLine?.C_UOM_ID?.id ?? 0,
        quantityEntered: orderLine?.QtyEntered ?? 0,
    });

    const olID = orderLineID ?? orderLine?.id;
    const lineKeyID = olID ?? 'p' + pid;
    const lineKey = getLineKey(lineKeyID);

    const lineLoading = useCheckoutStore(useCallback(
        state => state.loading?.[lineKey],
        [lineKey]
    ));
    const lineError = useCheckoutStore(useCallback(
        state => state.error?.[lineKey],
        [lineKey]
    ))

    const loading = useCheckoutStore(state => state.loading?.cart)
    const error = useCheckoutStore(state => state.error?.cart)

    const addToCart = useCheckoutStore(state => state.addToCart);

    const isOrderLine = () => {
        return orderLineID != null && orderLineID > 0;
    }

    const isInCart = () => {
        return hasOrderLine;
    }
    const getUom = (uomID) => {
        if(uomID === product?.uom?.id) {
            return product.uom;
        } else if(uomID === product?.caseUom?.id) {
            return product.caseUom;
        }
    }
    const getQuantityOrdered = (quantityEntered, uomID) => {
        const uom = getUom(uomID);
        const uomSize = uom ? uom.uomSize : 1;
        return quantityEntered * uomSize;
    }

    const getCartRecord = (quantityEntered, uomID, isBackOrdered) => {
        return {
            orderLineID: orderLine?.id ?? 0,
            productID: product.id,
            asiID: asiID,
            uomID: uomID,
            qtyEntered: quantityEntered,
            qtyOrdered: getQuantityOrdered(quantityEntered, uomID),
            product: product,
            isBackOrdered: isBackOrdered
        }
    }

    const updateOrderLine = (quantityEntered = purchaseInfo.quantityEntered, uomID = purchaseInfo.uomID, isBackOrdered) => {
        const cartItem = getCartRecord(quantityEntered, uomID, isBackOrdered);
        if(!lineLoading) {
            addToCart(session, cartItem);
        }
    }

    const setUomID = (uomID, isBackOrdered) => {
        if(hasOrderLine && product) {
            updateOrderLine(undefined, uomID, isBackOrdered);
        }
        setPurchaseInfo({
            ...purchaseInfo,
            uomID: uomID
        })
    }
    const setQuantityEntered = (quantityEntered, isBackOrdered) => {
        if(hasOrderLine && product) {
            updateOrderLine(quantityEntered, undefined, isBackOrdered);
        }
        setPurchaseInfo({
            ...purchaseInfo,
            quantityEntered: quantityEntered
        })
    }
    const setAsiID = (passedAsiID) => {
        if(passedAsiID === asiID) {
            // Do Nothing
        } else if(isOrderLine()) {
            throw new Error("Can't update the ASI ID on an order line");
        } else {
            setAsiIDState(passedAsiID)
            setPurchaseInfo({
                quantityEntered: -1,
                uomID: 0,
            })
        }
    }

    const isNotAvailable = () => {
        return !product;
    }

    const matchingUomID = orderLine?.C_UOM_ID?.id;
    const infoUomID = Boolean(matchingUomID) && purchaseInfo.uomID === 0 ? matchingUomID : purchaseInfo.uomID;
    const info = ProductPurchaseInfo.getInfo(product,
        asiID, infoUomID, purchaseInfo.quantityEntered, orderLine?.LineNetAmt);

    if(product) {
        if(info.getSelectedUom().id !== info.getSelectedUomID()) {
            setPurchaseInfo({
                ...purchaseInfo,
                uomID: info.getSelectedUom().id
            })
        }
    }

    const isBackOrdered = () => {
        return orderLine && orderLine.isbackorder;
    }

    const isUpdating = () => {
        return lineLoading;
    }

    const isLoading = () => {
        return isUpdating() || info.isLoadingPurchaseInfo()
    }

    const olOrdered = orderLine?.QtyEntered;
    useValueChangeListener(() =>
        setPurchaseInfo({...purchaseInfo, quantityEntered: olOrdered})
    , [olOrdered]);

    const dispatch = useDispatch();
    useValueChangeListener(() => {
        if(lineError) {
            dispatch(reportError({
                code: lineKey,
                error: getErrorMsg(lineError)
            }))
        }
        }, [lineError]);

    return {
        webProduct : {
            ...info,
            isInCart,
            isNotAvailable,
            isBackOrdered,
            isUpdating,
            isLoading,
            setQuantityEntered,
            setUomID,
            setAsiID,

            addProductToCart: updateOrderLine,
            loading,
            error,
            lineLoading,
            lineError,
        }
    }
}

const getErrorMsg = (lineError) => {
    if(!lineError) return;
    if(lineError.includes("Customer does not have valid AID")) {
        return "This product requires a valid AID and EOID";
    } else {
        return lineError;
    }
}

export default useWebProduct;
